/**
 * Cache Update Event System
 *
 * Lets components react when WebSocket events update the reports cache in place.
 * Debounces rapid updates to avoid excessive re-renders (e.g. many lock events per minute).
 */

/** Extra context for listeners. `reload360Lists: false` => 360 lists patch in place instead of refetching. */
export type CacheUpdateMeta = {
  reload360Lists: boolean;
};

type CacheUpdateListener = (
  affectedReportUuids: string[],
  meta: CacheUpdateMeta
) => void;

class CacheUpdateEventEmitter {
  private listeners: Set<CacheUpdateListener> = new Set();
  private updateQueue: Set<string> = new Set();
  private reload360Lists = true;
  private batchTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly BATCH_DELAY_MS = 100;

  subscribe(listener: CacheUpdateListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Notify listeners of a cache update. Multiple calls within BATCH_DELAY_MS are debounced.
   * Pass `{ reload360Lists: false }` for lock events that 360 lists update in place.
   */
  notify(reportUuid?: string, meta?: Partial<CacheUpdateMeta>): void {
    if (reportUuid) {
      this.updateQueue.add(reportUuid);
    }
    if (meta?.reload360Lists === false) {
      this.reload360Lists = false;
    }
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }
    this.batchTimeout = setTimeout(() => {
      this.flushUpdates();
    }, this.BATCH_DELAY_MS);
  }

  private flushUpdates(): void {
    const affectedReports = Array.from(this.updateQueue);
    const meta: CacheUpdateMeta = { reload360Lists: this.reload360Lists };
    this.updateQueue.clear();
    this.reload360Lists = true;
    this.batchTimeout = null;
    this.listeners.forEach((listener) => {
      try {
        listener(affectedReports, meta);
      } catch (error) {
        console.error("Error in cache update listener:", error);
      }
    });
  }
}

export const cacheUpdateEvents = new CacheUpdateEventEmitter();
